/**
 * One-line human echo of the settings a run used — "≤ 5 MB · 720p · 30 fps" —
 * so repeated runs of the same file are tellable apart on their result cards
 * (improvement-sweep P2 UX note; the re-run analysis showed ~1 in 4 users
 * iterate settings on the same file and converge via download).
 *
 * Curated, not exhaustive: only high-signal knobs render, mode-selection keys
 * gate which value-key is actually active, and sentinel values (AUTOMATIC,
 * SAME_AS_SOURCE…) stay silent. An empty echo means "defaults" — render nothing.
 */
const SENTINELS = new Set(['', 'null', 'undefined', 'AUTOMATIC', 'AUTO', 'DEFAULT', 'SAME_AS_SOURCE', 'NONE', 'ORIGINAL', 'UNCHANGED']);

function has(options: Record<string, any>, k: string): boolean {
    const v = options[k];
    return v !== undefined && v !== null && !SENTINELS.has(String(v));
}

function pretty(s: any): string {
    return String(s).toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function fps(raw: any): string {
    const s = String(raw);
    const m = s.match(/^(\d+)\/(\d+)$/);
    if (m) {
        const v = Number(m[1]) / Number(m[2]);
        return `${Number.isInteger(v) ? v : v.toFixed(1)} fps`;
    }
    return `${s} fps`;
}

/** A `text::unit` shell ("1280::PIXEL", "50::PERCENT") or a bare value, as a number and a unit. */
function shell(value: unknown): { n: number | null; unit: string } {
    const text = String(value ?? '');
    const i = text.indexOf('::');
    const n = Number(i >= 0 ? text.slice(0, i) : text);
    return { n: Number.isFinite(n) && n > 0 ? n : null, unit: i >= 0 ? text.slice(i + 2).toUpperCase() : '' };
}

function dim(value: unknown): string | null {
    const { n, unit } = shell(value);
    if (n === null) return null;
    return unit.startsWith('PERCENT') ? `${n} %` : String(n);
}

/**
 * The video resolution as a user reads it, from the xconfig mode id and its value key
 * (`VIDEO_RESOLUTION_*` — the same vocabulary `compression-group.component.tsx` maps to a frame).
 * A mode id is never shown: it is a machine name, and lower-casing it made "video_resolution_preset".
 */
export function resolutionEcho(options: Record<string, any>): string | null {
    const mode = String(options.videoResolution ?? '');
    if (!mode || SENTINELS.has(mode)) return null;
    const upper = mode.toUpperCase();
    if (upper === 'VIDEO_RESOLUTION_ORIGINAL') return null;
    if (upper === 'VIDEO_RESOLUTION_PRESET') return has(options, 'videoPreset') ? String(options.videoPreset).toLowerCase() : null;
    if (upper === 'VIDEO_FIXED_RESOLUTION_PRESET') {
        const [w, h] = String(options.videoFixedPreset ?? '').toLowerCase().split('x');
        return w && h ? `${w} × ${h}` : null;
    }
    if (upper === 'VIDEO_RESOLUTION_WIDTH_HEIGHT') {
        const w = dim(options.width);
        const h = dim(options.height);
        return w && h ? `${w} × ${h}` : null;
    }
    if (upper === 'VIDEO_RESOLUTION_WIDTH') { const w = dim(options.width); return w ? `${w} wide` : null; }
    if (upper === 'VIDEO_RESOLUTION_HEIGHT') { const h = dim(options.height); return h ? `${h} tall` : null; }
    if (upper === 'VIDEO_RESOLUTION_PERCENTAGE') { const p = shell(options.resolutionPercentage).n; return p === null ? null : `${p} %`; }
    // A mode this build does not know: a machine name is not a caption.
    if (/^VIDEO_/.test(upper) || /_/.test(mode)) return null;
    return mode.toLowerCase();
}

/**
 * UX round 2 (UX2-06): codec ids that are machine names ("PCM_S24LE") read as the option the user picked, in the option
 * tree's words ("PCM 24-bit Little Endian" there; little-endian is what WAV means, so the caption drops it). Every other
 * codec ("H264", "AAC", "MP3") already reads as a name and is shown as before.
 */
const CODEC_LABELS: Readonly<Record<string, string>> = {
    PCM_S16LE: 'PCM 16-bit',
    PCM_S24LE: 'PCM 24-bit',
    PCM_S32LE: 'PCM 32-bit',
    PCM_S16BE: 'PCM 16-bit Big Endian',
    PCM_ALAW: 'PCM A-law',
    PCM_MULAW: 'PCM mu-law',
    AMR_NB: 'AMR Narrow Band',
    AMR_WB: 'AMR Wide Band',
};

export function codecEcho(codec: unknown): string {
    const id = String(codec).toUpperCase();
    return CODEC_LABELS[id] ?? id;
}

export function formatOptionEcho(options: Record<string, any> | null | undefined): string | null {
    if (!options || typeof options !== 'object') return null;
    const chips: string[] = [];
    const v = (k: string) => options[k];

    // Compression mode gates its own value knob.
    const comp = String(v('compression') || '');
    if (comp === 'MAX_FILE_SIZE' && has(options, 'maxFileSize')) {
        chips.push(`≤ ${v('maxFileSize')} ${v('maxFileSizeUnit') || 'MB'}`);
    } else if (comp.includes('PERCENTAGE') && (has(options, 'sizePercentage') || has(options, 'percentage'))) {
        chips.push(`Max ${v('sizePercentage') ?? v('percentage')}%`);
    } else if (comp.includes('QUALITY_PRESET') && has(options, 'gifQualityPreset')) {
        chips.push(`${pretty(v('gifQualityPreset'))} quality`);
    } else if (comp === 'CONSTANT_BITRATE' && has(options, 'videoBitrate')) {
        chips.push(`${v('videoBitrate')} kbps`);
    } else if (comp && !SENTINELS.has(comp)) {
        chips.push(pretty(comp));
    }
    if (!comp && has(options, 'percentage')) chips.push(`Max ${v('percentage')}%`);
    if (has(options, 'quality') && !String(v('quality')).includes('SELECTION')) chips.push(`Quality ${v('quality')}`);

    // The resolution is a MODE id plus a value key (Studio round 6, SDE#12: the card read
    // "video_resolution_preset · H264"). Say the value the mode points at, in the user's words --
    // "1080p", "1280 × 720", "50 %" -- and nothing for a mode whose value is not here to read.
    const resolution = resolutionEcho(options);
    if (resolution) chips.push(resolution);
    else if (has(options, 'resolution') && !/SELECTION|RESOLUTION_/i.test(String(v('resolution')))) chips.push(String(v('resolution')).toLowerCase());

    if (String(v('videoFramerateSelection') || '') === 'FRAMERATE' && has(options, 'framerate')) {
        chips.push(fps(v('framerate')));
    }

    // A codec that merely restates the output extension is noise, not a choice.
    const toExt = String(v('toExtension') || '').toUpperCase();
    for (const ck of ['videoCodec', 'audioCodec']) {
        if (has(options, ck) && String(v(ck)).toUpperCase() !== toExt) chips.push(codecEcho(v(ck)));
    }
    if (has(options, 'audioBitrate')) chips.push(`${v('audioBitrate')} kbps`);
    if (has(options, 'sampleRate')) chips.push(`${v('sampleRate')} Hz`);
    if (has(options, 'dpi')) chips.push(`${v('dpi')} dpi`);
    if (has(options, 'colors')) chips.push(`${v('colors')} colors`);

    // De-dup, cap: four chips is a caption, more is a config dump.
    const unique = [...new Set(chips.filter(c => c && c.length <= 24))];
    return unique.length ? unique.slice(0, 4).join(' · ') : null;
}
